import { getAttachment } from "core/utils/aws/getAttachment";
import { downloadFile } from "core/utils/aws/downloadFile";
import { downloadAsDataUri } from "core/utils/aws/downloadAsDataUri";
import { AttachmentList } from "components/Attachments/AttachmentList";
import { IAttachment } from "components/Attachments/IAttachment";
import { AttachmentDetail } from "interfaces/attachment";
import { useContext, useState } from "react";
import { AxiosContext } from "contexts/AxiosContext";
import { useUnmount } from "react-use";
import { Box } from "@mui/material";

export interface ExistingAttachmentListProps {
  initialAttachments: AttachmentDetail[];
  setAttachmentIdsToDelete?: React.Dispatch<React.SetStateAction<number[]>>;
  setSelectedAttachmentIds?: React.Dispatch<React.SetStateAction<number[]>>;
  showUser?: boolean;
}

export const ExistingAttachmentsList = ({
  initialAttachments,
  setAttachmentIdsToDelete,
  setSelectedAttachmentIds,
  showUser = true
}: ExistingAttachmentListProps): JSX.Element => {
  const { axios } = useContext(AxiosContext)!;
  const controller = new AbortController();
  useUnmount(() => {
    controller.abort();
  });

  const [attachments, setAttachments] = useState<IAttachment[]>(
    initialAttachments.map(attachmentDetail => {
      const isImage = attachmentDetail.original_file.type.startsWith("image");
      const attachmentItem: IAttachment = {
        id: attachmentDetail.id,
        filename: attachmentDetail.original_file.name,
        type: attachmentDetail.original_file.type,
        attachedByUser: showUser ? attachmentDetail.create_user : undefined,
        onDelete: setAttachmentIdsToDelete
          ? () => {
              setAttachmentIdsToDelete(l => [...l, attachmentDetail.id]);
              setAttachments(l =>
                l.filter(attachment => attachment.id !== attachmentDetail.id)
              );
            }
          : undefined,
        downloadFile: async () => {
          const refetchData = await getAttachment(axios, attachmentDetail.id);
          downloadFile({
            url: refetchData.original_file.url,
            headers: refetchData.original_file.download_headers,
            signal: controller.signal,
            filename: attachmentDetail.original_file.name
          });
        },
        retrieveImagePreview: isImage
          ? async () => {
              const refetchData = await getAttachment(
                axios,
                attachmentDetail.id
              );
              const url = refetchData?.thumbnail_large?.url
                ? refetchData?.thumbnail_large.url
                : refetchData?.original_file.url ?? "";

              const headers = refetchData?.thumbnail_large?.download_headers
                ? refetchData?.thumbnail_large.download_headers
                : refetchData?.original_file.download_headers;

              return downloadAsDataUri({
                url,
                headers,
                signal: controller.signal
              });
            }
          : undefined
      };
      return attachmentItem;
    })
  );
  return (
    <Box sx={{ width: "100%" }}>
      <AttachmentList
        attachments={attachments}
        onAttachmentSelected={
          setSelectedAttachmentIds
            ? (attachment, selected) => {
                if (attachment.id !== undefined) {
                  selected
                    ? setSelectedAttachmentIds(l => [...l, attachment.id!])
                    : setSelectedAttachmentIds(l =>
                        l.filter(id => id !== attachment.id)
                      );
                }
              }
            : undefined
        }
      />
    </Box>
  );
};
ExistingAttachmentsList.displayName = "ExistingAttachmentsList";
